import { Layers, Plus } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useOutletContext } from 'react-router'

import { agencia, type FichaDeAgencia, type PlanDeAgencia, useFichaDeAgencia, usePlanesDeAgencia, useRefrescarAgencia } from '../../api/agencia'
import { ErrorDeLaApi } from '../../api/nucleo'
import { Aviso, Boton, Campo, Casilla, Esqueleto, EstadoVacio, Modal, Tarjeta, Titulo } from '../../componentes/ui'
import { listaDeModulos, nombreDeModulo } from '../facturacion'
import type { Contexto } from '../Modulo'

/**
 * «Mis planes» (CLAUDE.md §36, fase R3): los planes que la agencia vende a sus clientes. Cada número tiene
 * su máximo, que es el del plan Revendedor de la agencia; lo que no cabe lo rechaza el núcleo y aquí se
 * dice en llano. Lo que no se toca sale del techo.
 */
const NUMEROS: Array<{ clave: string; etiqueta: string; ayuda?: string }> = [
  { clave: 'sitiosMaximos', etiqueta: 'Webs por cliente' },
  { clave: 'creditosMensuales', etiqueta: 'Créditos de IA al mes' },
  { clave: 'cuotaDeMediosMB', etiqueta: 'Espacio para imágenes (MB)' },
]
const ALCANCES: Record<string, string> = { seccion: 'Esta sección', pagina: 'Esta página', sitio: 'Todo el sitio' }
const IDIOMAS: Record<string, string> = { en: 'Inglés', pt: 'Portugués' }

const Editor = ({ espacio, ficha, plan, cerrar, hecho }: { cerrar: () => void; espacio: number; ficha: FichaDeAgencia; hecho: () => void; plan: null | PlanDeAgencia }) => {
  const techo = ficha.techo!
  const [nombre, setNombre] = useState(plan?.nombre ?? '')
  const [precio, setPrecio] = useState(plan?.precioMensual != null ? String(plan.precioMensual) : '')
  const [numeros, setNumeros] = useState<Record<string, string>>(Object.fromEntries(NUMEROS.map(({ clave }) => [clave, String((plan?.[clave] ?? techo[clave] ?? '') as number | string)])))
  const [modulos, setModulos] = useState<string[]>(plan?.modulos ?? techo.modulos)
  const [alcances, setAlcances] = useState<string[]>(plan?.alcancesIAPermitidos ?? techo.alcancesIAPermitidos)
  const [idiomas, setIdiomas] = useState<string[]>(plan?.idiomas ?? techo.idiomas ?? [])
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)
  const alternar = (lista: string[], valor: string) => (lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor])
  const guardar = async (e: FormEvent) => {
    e.preventDefault()
    setEnviando(true)
    setFallo(null)
    try {
      await agencia(espacio).guardarPlan({
        ...(plan ? { id: plan.id } : {}),
        nombre,
        precioMensual: precio === '' ? null : Number(precio),
        modulos,
        alcancesIAPermitidos: alcances,
        idiomas,
        ...Object.fromEntries(NUMEROS.map(({ clave }) => [clave, numeros[clave] === '' ? undefined : Number(numeros[clave])])),
      })
      hecho()
    } catch (e) {
      setFallo(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido guardar el plan. Vuelve a intentarlo.')
    } finally {
      setEnviando(false)
    }
  }
  return (
    <Modal
      ancho="max-w-xl"
      cerrar={cerrar}
      pie={
        <div className="flex justify-end gap-2">
          <Boton onClick={cerrar}>Cancelar</Boton>
          <Boton cargando={enviando} form="plan-de-agencia" tipo="primario" type="submit">
            Guardar
          </Boton>
        </div>
      }
      titulo={plan ? `Plan «${plan.nombre}»` : 'Nuevo plan'}
    >
      <form className="flex flex-col gap-4" id="plan-de-agencia" onSubmit={guardar}>
        <Campo autoFocus etiqueta="Nombre" onChange={(e) => setNombre(e.target.value)} required value={nombre} />
        <Campo ayuda="Lo que cobras al mes. Cobrar desde el panel llegará pronto." etiqueta="Precio al mes" min={0} onChange={(e) => setPrecio(e.target.value)} step="0.01" type="number" value={precio} />
        <div className="grid gap-4 sm:grid-cols-3">
          {NUMEROS.map(({ clave, etiqueta }) => (
            <Campo
              ayuda={techo[clave] != null ? `Máximo ${Number(techo[clave]).toLocaleString('es')}` : undefined}
              etiqueta={etiqueta}
              key={clave}
              // Sin `max`: el aviso del navegador sale en su idioma; el del núcleo dice en llano qué pasa del techo.
              min={0}
              onChange={(e) => setNumeros({ ...numeros, [clave]: e.target.value })}
              type="number"
              value={numeros[clave]}
            />
          ))}
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[13px] font-medium">Qué incluye</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {techo.modulos.map((m) => (
              <label className="flex items-center gap-2 text-sm" key={m}>
                <Casilla marcada={modulos.includes(m)} onChange={() => setModulos(alternar(modulos, m))} />
                {nombreDeModulo(m)}
              </label>
            ))}
          </div>
        </fieldset>
        {techo.alcancesIAPermitidos.length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-[13px] font-medium">Diseño con IA</legend>
            <div className="flex flex-wrap gap-4">
              {techo.alcancesIAPermitidos.map((a) => (
                <label className="flex items-center gap-2 text-sm" key={a}>
                  <Casilla marcada={alcances.includes(a)} onChange={() => setAlcances(alternar(alcances, a))} />
                  {ALCANCES[a] ?? a}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {(techo.idiomas ?? []).length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-[13px] font-medium">Idiomas de sus webs, además del español</legend>
            <div className="flex flex-wrap gap-4">
              {(techo.idiomas ?? []).map((i) => (
                <label className="flex items-center gap-2 text-sm" key={i}>
                  <Casilla marcada={idiomas.includes(i)} onChange={() => setIdiomas(alternar(idiomas, i))} />
                  {IDIOMAS[i] ?? i}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
      </form>
    </Modal>
  )
}

export const Planes = () => {
  const { espacio } = useOutletContext<Contexto>()
  const { data: ficha, isPending: cargandoFicha } = useFichaDeAgencia(espacio)
  const { data: planes, isPending, error } = usePlanesDeAgencia(espacio)
  const refrescar = useRefrescarAgencia(espacio)
  const [abierto, setAbierto] = useState<null | PlanDeAgencia | 'nuevo'>(null)

  if (!cargandoFicha && !ficha?.puede.planes) return <Aviso tono="error">Esta cuenta no es una agencia, o no llevas sus planes.</Aviso>
  const puedeCrear = Boolean(ficha?.techo && ficha.agencia.activo)
  return (
    <>
      <Titulo
        accion={
          <Boton disabled={!puedeCrear} onClick={() => setAbierto('nuevo')} tipo="primario">
            <Plus aria-hidden className="size-4" /> Nuevo plan
          </Boton>
        }
        descripcion={ficha?.techo ? `Lo que ofreces a tus clientes. Nada pasa de tu plan «${ficha.techo.nombre}».` : undefined}
        rotulo={ficha?.agencia.nombre}
      >
        Mis planes
      </Titulo>
      {ficha && !ficha.techo && <Aviso tono="error">Tu cuenta no tiene un plan Revendedor: escribe al equipo de Wixy.</Aviso>}
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {isPending ? (
          <Esqueleto />
        ) : error ? (
          <Aviso tono="error">No hemos podido leer tus planes. Vuelve a intentarlo en un momento.</Aviso>
        ) : planes!.length === 0 ? (
          <div className="md:col-span-2">
            <EstadoVacio
              accion={
                <Boton disabled={!puedeCrear} onClick={() => setAbierto('nuevo')} tipo="primario">
                  <Plus aria-hidden className="size-4" /> Crear el primero
                </Boton>
              }
              descripcion="Un plan dice cuántas webs, créditos de IA y espacio tiene cada cliente, y qué incluye. Con uno basta para empezar."
              icono={<Layers />}
              titulo="Todavía no tienes planes"
            />
          </div>
        ) : (
          planes!.map((p) => (
            <Tarjeta key={p.id}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-base font-semibold">{p.nombre}</h3>
                  <p className="text-[13px] text-tenue">
                    {p.precioMensual ? `${p.precioMensual} al mes · ` : ''}
                    {p.clientes === 1 ? '1 cliente' : `${p.clientes} clientes`}
                  </p>
                </div>
                <Boton disabled={!ficha?.agencia.activo} onClick={() => setAbierto(p)} pequeno>
                  Editar
                </Boton>
              </div>
              <p className="mt-3 text-[13px] text-tinta-2">
                {p.sitiosMaximos === 1 ? '1 web' : `${p.sitiosMaximos ?? 1} webs`} · {Number(p.creditosMensuales ?? 0).toLocaleString('es')} créditos al mes · {Number(p.cuotaDeMediosMB ?? 0).toLocaleString('es')} MB
              </p>
              {p.modulos?.length ? <p className="mt-1 text-xs text-tenue">{listaDeModulos(p.modulos)}</p> : null}
            </Tarjeta>
          ))
        )}
      </div>
      {abierto && ficha?.techo && (
        <Editor
          cerrar={() => setAbierto(null)}
          espacio={espacio}
          ficha={ficha}
          hecho={() => {
            setAbierto(null)
            void refrescar()
          }}
          plan={abierto === 'nuevo' ? null : abierto}
        />
      )}
    </>
  )
}
